import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Search, RefreshCw, Eye, FileText } from 'lucide-react';
import { toast } from 'sonner';

const AuditLogs = () => {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableFilter, setTableFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tableFilter) params.append('table_name', tableFilter);
      if (actionFilter) params.append('action', actionFilter);
      params.append('limit', '200');
      
      const response = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [api, tableFilter, actionFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const viewDetails = (log) => {
    setSelectedLog(log);
    setIsDialogOpen(true);
  };

  const formatJson = (jsonString) => {
    if (!jsonString) return 'N/A';
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  const getActionBadge = (action) => {
    switch (action) {
      case 'CREATE':
        return <span className="badge-success">{action}</span>;
      case 'UPDATE':
        return <span className="badge-warning">{action}</span>;
      case 'DELETE':
        return <span className="badge-danger">{action}</span>;
      default:
        return <span className="badge-info">{action}</span>;
    }
  };

  const tables = [
    'organizations', 'branches', 'stores', 'roles', 'employees',
    'customers', 'vendors', 'uoms', 'items', 'machines',
    'parts', 'part_prices', 'cycle_times', 'rejection_reasons'
  ];

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="audit-logs">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <FileText className="w-6 h-6 text-sky-500" />
            Audit Logs
          </h1>
          <p className="text-slate-500 text-sm">{logs.length} records</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="w-48">
              <Select value={tableFilter} onValueChange={setTableFilter}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Filter by Table" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Tables</SelectItem>
                  {tables.map(table => (
                    <SelectItem key={table} value={table}>{table}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Filter by Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Actions</SelectItem>
                  <SelectItem value="CREATE">CREATE</SelectItem>
                  <SelectItem value="UPDATE">UPDATE</SelectItem>
                  <SelectItem value="DELETE">DELETE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={fetchLogs}
              className="rounded-sm"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No audit logs found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>ID</TableHead>
                    <TableHead>Table</TableHead>
                    <TableHead>Record ID</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Changed By</TableHead>
                    <TableHead>Changed At</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead className="text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-xs">{log.id}</TableCell>
                      <TableCell className="font-medium">{log.table_name}</TableCell>
                      <TableCell className="font-mono text-xs">{log.record_id}</TableCell>
                      <TableCell>{getActionBadge(log.action)}</TableCell>
                      <TableCell>{log.changed_by || 'System'}</TableCell>
                      <TableCell className="text-sm">
                        {log.changed_at ? new Date(log.changed_at).toLocaleString() : '-'}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{log.ip_address || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => viewDetails(log)}
                          className="h-8 w-8"
                          data-testid={`view-${log.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Log Details</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Table</label>
                  <p className="font-medium">{selectedLog.table_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Record ID</label>
                  <p className="font-mono">{selectedLog.record_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Action</label>
                  <p>{getActionBadge(selectedLog.action)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Changed At</label>
                  <p>{selectedLog.changed_at ? new Date(selectedLog.changed_at).toLocaleString() : '-'}</p>
                </div>
              </div>
              
              {selectedLog.old_values && (
                <div>
                  <label className="text-sm font-medium text-slate-500 block mb-2">Old Values</label>
                  <pre className="bg-slate-100 p-3 rounded-sm text-xs overflow-x-auto font-mono">
                    {formatJson(selectedLog.old_values)}
                  </pre>
                </div>
              )}
              
              {selectedLog.new_values && (
                <div>
                  <label className="text-sm font-medium text-slate-500 block mb-2">New Values</label>
                  <pre className="bg-slate-100 p-3 rounded-sm text-xs overflow-x-auto font-mono">
                    {formatJson(selectedLog.new_values)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogs;
